import * as React from 'react';
import { scrollIntoView } from 'app/infrastructure/platform/browser-dom';
import { ComplexValue, RowComponentProps } from 'ui/common/types';
import { ButtonTertiary } from 'ui';
import styles from './tile.scss';
import { debounceRender } from 'ui/common/debounce-render';
import { ExpanderBackground } from 'ui/common/dialog-expander/expander-background/expander-background';
import { ContextMenu } from 'ui/tile/context-menu';
import { ButtonSecondary, ValidationLevel } from 'ui/common';
import { OpenStateInjectedProps, withOpenState } from 'ui/common/enhancers';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { CopyIteratorTranslations } from 'app/area/case/selectors';
import { MdEdit } from 'react-icons/md';
import { ModalSize } from 'app/components/constants';
import { DialogExpander } from 'ui/common/dialog-expander/dialog-expander';

const DefaultHeader = ({primaryKey, showPrimaryKey}) =>  primaryKey && showPrimaryKey ?
  <div className={`${styles.key}`}>#{primaryKey.value}</div> : null;

class TileComponent extends React.Component<TileProps & OpenStateInjectedProps, TileState> {
  private ref: HTMLElement;
  public static defaultProps: Partial<TileProps> = {
    enabledChildEntityOperations: { remove: false },
    textKeyLabel: ''
  };

  public state: Readonly<TileState> = {
    hideImmediately: false
  };

  private handleSelect = () => {
    this.setState({hideImmediately: false}, () =>
      this.props.onSelectDocumentItem && this.props.onSelectDocumentItem()
    );
    !this.props.expandAll && this.ref && scrollIntoView(this.ref);
    this.props.open();
  };

  private handleEdit = () => {
    this.props.onEditDocumentItem && this.props.onEditDocumentItem();
  };

  private handleDelete = () => {
    this.props.onDeleteDocumentItem && this.props.onDeleteDocumentItem();
  };

  private handleClose = () => {
    this.props.onSelectDocumentItem?.(); //select the active tile to force the route to update itself with the correct section
    this.setState({hideImmediately: true}, () =>
      this.props.onSelectDocumentItem?.(null)); //select null to clear params like 'TblAction/1' etc from the route    
    this.props.close();
  };  

  private renderEditDetailsButton = () => {
    const {shouldEditDocumentItem, textDetailsEditButton, shouldHideEditDocumentIcon} = this.props;   
    const onClickEditIcon = shouldEditDocumentItem ? this.handleEdit : this.handleSelect;

    return <>
      {shouldHideEditDocumentIcon ? null :
        <div className={`${styles.buttonContainer}`} title={textDetailsEditButton} onClick={onClickEditIcon}>
          <MdEdit className={styles.buttonIcons}/> 
        </div>}
    </>;
  };

  private renderViewDetailsButton = () => {
    const { textShowDetails } = this.props;
    const childrenHasErrors = !!this.props.validationDescendants?.length && 
      this.props.validationDescendants.some(v => v.level === ValidationLevel.error);

    const childrenHasWarnings = !!this.props.validationDescendants?.length && 
      this.props.validationDescendants.some(v => v.level === ValidationLevel.warning);

    const validationClass = childrenHasErrors ?
      styles.hasChildErrors : (childrenHasWarnings ? styles.hasChildWarnings : '');    
    
    return <>
      <div className={`${styles.buttonContainer}`} onClick={this.handleSelect}>
        <ButtonTertiary classNames={`${styles.detailsButton} ${validationClass}`} onClick={this.handleSelect}>
          {textShowDetails}
        </ButtonTertiary>
      </div>
    </>;
  };

  private addRef = (el: HTMLDivElement | null) => {
    this.ref = el;
  };

  // The Tile has become to procedurally heavy.
  // It takes on too many concerns and carries the logical weight of all it's use cases combined.
  // Refactoring should take place when changing the UX from expander to modal dialogs.
  // The inline comments are added to assist that refactoring later on.

  public render() {
    const {
      componentId,
      details,
      children,
      activate,
      primaryKey,
      showPrimaryKey = true,
      showDetailsButton = true,
      showDetailsEditButton = false,
      textKeyLabel,
      textDone,
      textDetailsEditButton,
      print,
      expandAll,
      isOpen
    } = this.props;
    // Detail dialog styles and title when primaryKey prop is provided
    const primaryKeyValue = (primaryKey && primaryKey.value) || '';
    const dialogTitle = `${textKeyLabel} #${primaryKeyValue}`;
    const titleStyle = React.Children.toArray(children).length > 1 ? 'has-title' : '';
    const tileContentClassNamesWhenPrimaryKeyIsShown = (showPrimaryKey && primaryKey) ? styles.tileWithKey : '';

    // Specific styles when a context menu is rendered
    const contextMenuClassNames = this.props?.enabledChildEntityOperations?.remove ? styles.hasMeatball : '';

    const canActivate = showDetailsButton && !this.state.hideImmediately;
    const isActivated = canActivate && activate;
    const shouldRenderOpenDetailView = details && (print || expandAll || isActivated);

    const shouldRenderDetailsEditButton = shouldRenderOpenDetailView && showDetailsEditButton;
    const detailsEditButton = shouldRenderDetailsEditButton ?
      <ButtonSecondary classNames={styles.editButton} onClick={this.handleEdit}>{textDetailsEditButton}</ButtonSecondary> : null;

    // From above logical evaluations; construct a expander to display details
    const detailViewExpanded = shouldRenderOpenDetailView ?
                                <DialogExpander
                                  expandAll={true}
                                  onClose={this.handleClose}
                                  title={dialogTitle}
                                  hideTitle={false}
                                  textDone={textDone}
                                  extraFooterActions={[detailsEditButton]}
                                  className="tile-details-view">
                                  {expandAll && <ContextMenu {...this.props} onDelete={this.handleDelete}/>}
                                  {details}
                                  {expandAll && <div className={styles.editButtonExpandAll}>{detailsEditButton}</div>}
                                </DialogExpander>
                              : null;

    const detailView = shouldRenderOpenDetailView ?
                          <ModalWrapper
                            onClose={this.handleClose}
                            onDone={this.handleClose}
                            headerTitle={dialogTitle}
                            btnTitle={textDone}
                            btnExtraFooterActionOverride={detailsEditButton}
                            size={ModalSize.xtraLarge}
                            textClose={textDone}>
                            {details}
                          </ModalWrapper>
                        : null;

    const editDetailsButton = showDetailsEditButton && details && this.renderEditDetailsButton();

    // Details wrapper style
    const openDetailWrapperStyles = shouldRenderOpenDetailView && !expandAll ? styles.activeTile : '';

    // Tile styling - conditional based on open state + conditional child components to render.
    const tileWrapperClasses = `${this.props.className} ${styles.tileWrapper} ${openDetailWrapperStyles}`;
    const viewDetailsButton = showDetailsButton && details && this.renderViewDetailsButton();
    const withShowDetailsButtonStyle = showDetailsButton && details && !expandAll && styles.withShowDetailsButton;

    if (expandAll && details) {
      // Only show details - no header, no context menu - no modal backdrop
      return <>
        <div id={componentId} ref={this.addRef} className={tileWrapperClasses}>
          {detailViewExpanded}
          {isActivated && 
            <span className={styles.activatedDetailView}>
              {detailView}
            </span>}
        </div>
      </>;
    }
  
    return  <>
      {/* When expanded/open - render the close handle before the tile */}
      {shouldRenderOpenDetailView ? <div tabIndex={0} onFocus={this.handleClose}/> : ''}

      <div id={componentId} className={tileWrapperClasses}>
        <div className={`${styles.tile} ${contextMenuClassNames} ${withShowDetailsButtonStyle}`}>
          <div className={`${styles.tileContent} ${tileContentClassNamesWhenPrimaryKeyIsShown}`}>
            {/* Tile content - header, context menu and children*/}
            <DefaultHeader {...this.props as any} />
            <div className={styles.btnIconsWrapper}>
              {viewDetailsButton}
              {editDetailsButton}
              <ContextMenu {...this.props} onDelete={this.handleDelete}/>
            </div>
            <div className={`tilevalues ${styles.tileValuesWrapper} ${titleStyle}`}>
              {children}
            </div>
          </div>
        </div>
        {/* If expanded (active, expand-all, print) - renders the details */}
        {(isOpen || shouldRenderOpenDetailView) && detailView}
        {/* Modal backdrop */}
        {(shouldRenderOpenDetailView && !(print || expandAll)) && <ExpanderBackground onClick={this.handleClose}/>}
      </div>
    </>;
  }
}

/**
 * Required translated texts for this component
 */
export interface TileTranslations {
  textDelete: string;
  textDeleteMessage: string;
  textContinue: string;
  textCancel: string;
  textKeyLabel?: string;
  textDone?: string;
  textShowDetails?: string;
  textHideDetails?: string;
  textDetailsEditButton?: string;
}

/**
 * @interface TileProps
 * Required properties for Tile component.
 */
export interface TileProps extends RowComponentProps, TileTranslations {
  primaryKey: ComplexValue<number>;
  showPrimaryKey?: boolean;
  showPrimaryKeyLongDescription?: boolean;
  linkifyLongDescription?: boolean;
  showDetailsButton?: boolean;
  showDetailsEditButton?: boolean;
  extendedContextMenuOperations?: { type: string; text: string; callback: () => void; icon?: React.ReactNode }[];
  print: boolean;
  expandAll: boolean;

  copyIterator?: CopyIteratorTranslations;
  textSubmit?: string;
  textSubmitInfo?: string;
  onSubmitDocument: (continueEditing?: boolean) => void;
  textDeleteCaseCancel?: string;
  textDeleteCaseCancelInfo?: string;
  onCloseDocument: () => void;

  shouldEditDocumentItem?: boolean;
  shouldRenderSubmitButton?: boolean;
  shouldHideEditDocumentIcon?: boolean;
  fetching?: boolean;
  disabledOverlay?: boolean;
  showDetailsHeader?: boolean;
  url?: string;
}

export interface TileState {
  /**
   * When expanded, don't wait the redux store update. Just close the view. UX optimization.
   */
  hideImmediately?: boolean;
}

const t = withOpenState()(debounceRender(TileComponent));
export {t as Tile};
